import React, { useContext, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AuthContext } from "components/authProvider/authProvider";
import { host } from "../../config";
import { getAuth, signOut } from "firebase/auth";
import { Navigate } from "react-router-dom";

const FAQ: React.FC = () => {
  const authContext = useContext(AuthContext); // Get Auth context
  const [checked, setChecked] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const { role, currentUser, suspended } = authContext;

  const auth = getAuth();
  if (suspended == true) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    currentUser
      .getIdToken(false)
      .then((idToken: string) => {
        fetch(`${host}/api/firebase/api/accept-tc`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${idToken}`,
          },
        })
          .then((response: Response) => {
            if (!response.ok) {
              response.text().then((text) => {
                console.log(text);
                alert("An error has occured while trying to accept the terms and conditions.");
              });
            }
            setIsLoading(false);
          })
          .catch((error) => {
            setIsLoading(false);
            alert("An error has occured while trying to accept the terms and conditions.");
            console.log(error);
          })
          .finally(() => {
            setIsLoading(false);
            console.log("Processing finished");
            if (role === "rating") {
              window.location.href = "/rating";
            } else {
              window.location.href = "/basePage";
            }
          });
      })
      .catch((error: any) => {
        console.log(error);
      });
  };
  const handleCheckBoxChange = (event) => {
    setChecked(event.target.checked);
  };

  const ulStyles = {
    marginBottom: 0,
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }
  return (
    <Container maxWidth="md" className="faq-container">
      {role === "employer" ? (
        <Box sx={{ my: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Technology Terms and Conditions for Accessing Protected Health Information via Web Portal
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            1. Purpose and Scope
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            These Technology Terms and Conditions ("Terms") govern access to and use of the secure web-based portal (the
            "Portal") provided by CarynHealth Management Services (the "Covered Entity") for the purpose of accessing,
            transmitting, or maintaining Protected Health Information ("PHI") as defined under the Health Insurance
            Portability and Accountability Act of 1996 ("HIPAA") and its implementing regulations (45 CFR Parts 160 and
            164).
            <br />
            These Terms apply to workforce members, contractors, business associates, and any other authorized users
            ("Users"). By accessing or using the Portal, you acknowledge that you have read, understand, and agree to be
            bound by these Terms.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            2. HIPAA Compliance Obligations
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Users acknowledge that PHI accessed through the Portal is subject to HIPAA and applicable state privacy
            laws. Users agree to:
            <ul style={ulStyles}>
              <li>Use and disclose PHI only as permitted by HIPAA and the Covered Entity's policies</li>
              <li>Access only the minimum necessary PHI required to perform authorized duties</li>
              <li>Immediately report any suspected or actual unauthorized access, use, or disclosure of PHI</li>
            </ul>
            Failure to comply may result in disciplinary action, termination of access, and potential civil or criminal
            penalties.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            3. Authorized Use
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Access to the Portal is granted solely for legitimate healthcare operations, treatment, payment, or other
            purposes expressly authorized by the Covered Entity. Users may not use the Portal for personal, improper, or
            unauthorized purposes.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            4. User Credentials and Authentication
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Users are responsible for safeguarding all authentication credentials. You agree that:
            <ul style={ulStyles}>
              <li>Credentials are individual and may not be shared</li>
              <li>Multi-factor authentication requirements must be followed</li>
              <li>Any compromise of credentials must be reported immediately</li>
            </ul>
            Users are responsible for all activity conducted under their credentials.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            5. Confidentiality and Safeguards
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Users agree to implement and maintain reasonable administrative, physical, and technical safeguards to
            protect PHI, including:
            <ul style={ulStyles}>
              <li>Accessing the Portal only from secure, private devices and networks</li>
              <li>Preventing viewing of PHI by unauthorized individuals</li>
              <li>Not storing PHI locally unless expressly permitted and encrypted</li>
            </ul>
            These obligations survive termination of access.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            6. Security Controls and Monitoring
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            The Covered Entity reserves the right to implement security controls and to monitor, log, audit, and review
            Portal activity to ensure compliance with HIPAA, these Terms, and internal policies. Users acknowledge there
            is no expectation of privacy when using the Portal.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            7. Prohibited Activities
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Users may not:
            <ul style={ulStyles}>
              <li>Attempt to access PHI beyond authorized permissions</li>
              <li>Circumvent security safeguards or monitoring</li>
              <li>Introduce malware or malicious code</li>
              <li>Use the Portal in violation of HIPAA or applicable laws</li>
            </ul>
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            8. Breach Reporting
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Users must immediately notify the Covered Entity of any suspected or confirmed security incident or breach
            involving PHI, in accordance with the Covered Entity's incident response and breach notification policies.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            9. Availability and Suspension of Access
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            The Covered Entity does not guarantee uninterrupted availability of the Portal. Access may be suspended or
            terminated at any time due to maintenance, security concerns, or violations of these Terms.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            10. Sanctions and Enforcement
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Violation of these Terms or applicable HIPAA requirements may result in sanctions, including revocation of
            access, disciplinary action, termination of employment or contract, and reporting to regulatory authorities
            as required by law.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            11. Limitation of Liability
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            To the extent permitted by law, the Covered Entity shall not be liable for damages resulting from
            unauthorized access caused by factors beyond its reasonable control, provided required HIPAA safeguards are
            maintained.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            12. Governing Law
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            These Terms shall be governed by the laws of the state of Georgia, without regard to conflict-of-law
            principles.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            13. Acceptance
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            By accessing or using the Portal, you acknowledge and agree to these HIPAA-compliant Technology Terms and
            Conditions.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ my: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Technology Terms and Conditions for Vendor Access to Protected Health Information via Web Portal
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            1. Purpose and Scope
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            These Technology Terms and Conditions ("Terms") govern access to and use of the secure web-based portal (the
            "Portal") provided by CarynHealth Management Services (the "Covered Entity") by third-party vendors,
            contractors, consultants, and service providers (collectively, "Vendors") for the purpose of accessing,
            transmitting, maintaining, or supporting Protected Health Information ("PHI") as defined under the Health
            Insurance Portability and Accountability Act of 1996 ("HIPAA") and its implementing regulations (45 CFR
            Parts 160 and 164).
            <br />
            These Terms apply to all Vendor personnel who are granted Portal access. By accessing or using the Portal,
            Vendor and its authorized users ("Vendor Users") acknowledge and agree to be bound by these Terms.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            2. Relationship to Business Associate Agreement
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Where Vendor access involves PHI, Vendor represents and warrants that a valid Business Associate Agreement
            ("BAA") is in effect between Vendor and the Covered Entity. These Terms supplement, and do not replace, the
            BAA. In the event of a conflict, the BAA shall control with respect to HIPAA obligations.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            3. HIPAA and Regulatory Compliance
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Vendor Users acknowledge that PHI accessed through the Portal is subject to HIPAA and applicable state
            privacy laws. Vendor agrees to:
            <ul style={ulStyles}>
              <li>Use and disclose PHI only as permitted by the BAA and HIPAA</li>
              <li>Limit access to the minimum necessary PHI required to perform contracted services</li>
              <li>Ensure Vendor Users receive HIPAA and security awareness training appropriate to their role</li>
              <li>Comply with all Covered Entity policies provided to Vendor</li>
            </ul>
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            4. Authorized Use
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Portal access is granted solely for purposes of performing services under a written agreement with the
            Covered Entity. Vendor Users may not use the Portal for any personal, competitive, or unauthorized purpose.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            5. User Credentials and Access Controls
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Vendor is responsible for ensuring that:
            <ul style={ulStyles}>
              <li>Portal credentials are issued only to authorized Vendor Users with a legitimate business need</li>
              <li>Credentials are not shared and are promptly disabled when no longer required</li>
              <li>Multi-factor authentication and access control requirements are followed</li>
            </ul>
            Vendor is responsible for all activity conducted using Vendor-issued credentials.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            6. Safeguards and Security Requirements
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Vendor agrees to implement and maintain appropriate administrative, physical, and technical safeguards to
            protect PHI, including but not limited to:
            <ul style={ulStyles}>
              <li>Accessing the Portal only from secure, managed devices and networks</li>
              <li>Encrypting PHI at rest and in transit where applicable</li>
              <li>Preventing storage of PHI on local devices unless expressly authorized</li>
            </ul>
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            7. Monitoring, Audit, and Compliance Reviews
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            The Covered Entity reserves the right to monitor, log, audit, and review Vendor Portal activity to verify
            compliance with these Terms, the BAA, and HIPAA requirements. Vendor agrees to cooperate with reasonable
            audits and compliance inquiries.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            8. Incident and Breach Reporting
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Vendor must immediately, and in no event later than the timeframe specified in the BAA, report any suspected
            or confirmed security incident or breach involving PHI accessed through the Portal. Vendor shall cooperate
            fully in investigation, mitigation, and required notifications.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            9. Suspension and Termination of Access
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            The Covered Entity may suspend or terminate Vendor or Vendor User access to the Portal at any time due to
            security concerns, contract termination, or violation of these Terms, the BAA, or applicable law.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            10. Sanctions and Remedies
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            Violation of these Terms may result in revocation of access, contractual remedies, indemnification
            obligations, and reporting to regulatory authorities as required by law.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            11. Limitation of Liability
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            To the extent permitted by law and subject to the BAA, the Covered Entity shall not be liable for damages
            arising from Vendor misuse of the Portal or Vendor failure to maintain required safeguards.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            12. Governing Law
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            These Terms shall be governed by the laws of the state of Georgia, without regard to conflict-of-law
            principles.
          </Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
            13. Acceptance
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            By accessing or using the Portal, Vendor and Vendor Users acknowledge and agree to these HIPAA-compliant
            Technology Terms and Conditions for Vendor Access.
          </Typography>
        </Box>
      )}
      <form onSubmit={handleSubmit} style={{ gap: "0px" }}>
        <label style={{ flexDirection: "row", columnGap: "1%" }}>
          <input type="checkbox" checked={checked} onChange={handleCheckBoxChange} />I agree to the above terms and
          conditions.
        </label>
        <button type="submit" disabled={!checked}>
          Submit
        </button>
      </form>
    </Container>
  );
};

export default FAQ;
